import React, { useMemo } from 'react'
import {
    Box,
    Text,
    Button,
    Link as ChakraLink,

    /* Hooks */
    useMultiStyleConfig
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'

/* Utils */
import { useIntl } from 'react-intl'
import { generateStyles } from '../../utils/utils'

import {sendClickEvent} from '../../../../utils/gtm-utils'
import {openClaraChat} from '../../../../hooks/use-clara-chat'

/* Contexts */
import { useGetLinkData } from '../../../../contexts/category-url-context'

const CtaBlock = ({
    Link,
    PreTitle,
    PreTitleStyle,
    Title,
    TitleStyle,
    Description,
    DescriptionStyle,
    Styling,
    CtaLinkText,
    BlockWidth,
    ContainerCustomStyling = {},
    ButtonCustomStyling = {},
    position = 'static',
    variant = Styling || 'Button',
    ...props
}) => {
    const intl = useIntl()
    const getLinkData = useGetLinkData()

    const linkData = useMemo(() => {
        return getLinkData(Link?.Expanded)
    }, [Link?.Expanded])

    const styles = useMultiStyleConfig('CtaBlock', {
        variant,
        ...props,
        BlockWidth: BlockWidth,
        position: position,
        ButtonCustomStyling: ButtonCustomStyling,
    });

    const fallbackContent = {
        ['cms.CtaBlock.CtaLinkText.ShopNow']: intl.formatMessage({
            id: 'cms.CtaBlock.CtaLinkText.ShopNow',
            defaultMessage: 'Shop Now'
        })
    }

    // GTM Modification
    const sendGTMEvent = (text) => {
        if (!text || text.toLowerCase().indexOf('shop now') >= 0) {
            sendClickEvent('start_shopping_clicked')
        }
    }

    const isOpenChat = linkData?.isOpenChat || false
    const isExternalUrl = linkData?.isExternalUrl || false
    const hasLink = !!linkData?.generatedURL
    const linkComponent = isOpenChat ? 'button' : hasLink ? (isExternalUrl ? ChakraLink : RouterLink) : 'span'
    const linkProps = isOpenChat
        ? { onClick: openClaraChat }
        : hasLink
            ? {
                ...(isExternalUrl ? { href: linkData?.generatedURL } : { to: linkData?.generatedURL }),
                ...(!linkData.stayOnSamePage && {
                    target: linkData?.target,
                    rel: linkData?.rel
                })
            }
            : {}

    return (
        <Box {...styles.container} {...ContainerCustomStyling} data-block-type="CtaBlock">
            {/* Pre title */}
            {PreTitle !== '' &&
                <Text {...generateStyles(PreTitleStyle, { ...styles.PreTitleStyle })}>
                    {PreTitle}
                </Text>
            }

            {/* Title */}
            {Title !== '' &&
                <Text {...generateStyles(TitleStyle, { ...styles.TitleStyle })}>
                    {Title}
                </Text>
            }

            {/* Description */}
            {Description !== '' &&
                <Text {...generateStyles(DescriptionStyle, { ...styles.DescriptionStyle })}>
                    {Description}
                </Text>
            }

            {Styling === 'Text' &&
                <Button
                    as={linkComponent}
                    variant="link"
                    {...linkProps}
                    alt={linkData?.AltText}
                    {...generateStyles({ ...props }, { ...styles.link })}
                    onClick={() => sendGTMEvent(CtaLinkText)}
                >
                    {CtaLinkText || fallbackContent['cms.CtaBlock.CtaLinkText.ShopNow']}
                </Button>
            }

            {Styling === 'Outlined' &&
                <Button
                    as={linkComponent}
                    variant="outline"
                    {...linkProps}
                    alt={linkData?.AltText}
                    {...generateStyles({ ...props }, { ...styles.cta })}
                    sx={styles.cta}>
                    { CtaLinkText || 'Outlined CTA'}
                </Button>
            }

            {Styling === 'Button' &&
                <Button
                    {...generateStyles({ ...props }, { ...styles.cta })}
                    {...ButtonCustomStyling}
                    as={linkComponent}
                    variant='outline'
                    {...linkProps}
                    alt={linkData?.AltText}
                    onClick={() => sendGTMEvent(CtaLinkText)}
                    sx={styles.cta}
                >
                    {CtaLinkText || fallbackContent['cms.CtaBlock.CtaLinkText.ShopNow']}
                </Button>
            }
        </Box>
    )
}

CtaBlock.propTypes = {
    Link: PropTypes.object,
    PreTitle: PropTypes.string,
    PreTitleStyle: PropTypes.object,
    Title: PropTypes.string,
    TitleStyle: PropTypes.object,
    Description: PropTypes.string,
    DescriptionStyle: PropTypes.object,
    Styling: PropTypes.string,
    CtaLinkText: PropTypes.string,
    BlockWidth: PropTypes.string,
    ContainerCustomStyling: PropTypes.object,
    ButtonCustomStyling: PropTypes.object,
    position: PropTypes.string,
    variant: PropTypes.string,
}

export default CtaBlock

